/**
 * Units (G-2, G-3). Money is integer santim, milk is integer milliliters.
 * User input is always parsed from the string — never `parseFloat(x) * 1000`.
 */

export class InvalidNumberError extends Error {
  constructor(readonly input: string) {
    super(`Invalid number: "${input}"`);
    this.name = 'InvalidNumberError';
  }
}

function normalizeDecimal(input: string): string {
  return input.trim().replace(/\s+/g, '').replace(',', '.');
}

/** "7.5" → 7500 ml; "7" → 7000; "" → null; "7.55" → error (one decimal only). */
export function parseLitersInput(input: string): number | null {
  const s = normalizeDecimal(input);
  if (s === '') return null;
  const m = /^(\d{0,6})(?:\.(\d))?$/.exec(s);
  if (!m || (m[1] === '' && m[2] === undefined)) throw new InvalidNumberError(input);
  const whole = m[1] === '' ? 0 : Number(m[1]);
  const tenth = m[2] === undefined ? 0 : Number(m[2]);
  return whole * 1000 + tenth * 100;
}

/** Non-throwing variant for forms. */
export function tryParseLiters(input: string): { ok: true; ml: number | null } | { ok: false } {
  try {
    return { ok: true, ml: parseLitersInput(input) };
  } catch {
    return { ok: false };
  }
}

/** "65.50" → 6550 santim; "65" → 6500; "1,234.50" → 123450; "" → null; "1.234" → error. */
export function parseBirrInput(input: string): number | null {
  let s = input.trim().replace(/\s+/g, '');
  if (s === '') return null;
  if (s.includes(',') && s.includes('.')) {
    s = s.replace(/,/g, '');
  } else if (s.includes(',')) {
    // A single comma followed by 1–2 digits is a decimal comma; otherwise thousands separators.
    s = /^\d+,\d{1,2}$/.test(s) ? s.replace(',', '.') : s.replace(/,/g, '');
  }
  const m = /^(\d{0,10})(?:\.(\d{1,2}))?$/.exec(s);
  if (!m || (m[1] === '' && m[2] === undefined)) throw new InvalidNumberError(input);
  const whole = m[1] === '' ? 0 : Number(m[1]);
  const frac = m[2] === undefined ? 0 : Number(m[2].padEnd(2, '0'));
  return whole * 100 + frac;
}

export function tryParseBirr(input: string): { ok: true; santim: number | null } | { ok: false } {
  try {
    return { ok: true, santim: parseBirrInput(input) };
  } catch {
    return { ok: false };
  }
}

function groupThousands(n: number): string {
  return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

/** 7500 → "7.5" (liters, one decimal, rounded half away from zero). */
export function formatLiters(ml: number): string {
  const sign = ml < 0 ? '-' : '';
  const tenths = Math.round(Math.abs(ml) / 100);
  return `${sign}${groupThousands(Math.trunc(tenths / 10))}.${tenths % 10}`;
}

/** Liters value for an input field (no thousands separators): 7500 → "7.5". */
export function litersInputValue(ml: number | null | undefined): string {
  if (ml === null || ml === undefined) return '';
  const tenths = Math.round(ml / 100);
  return `${Math.trunc(tenths / 10)}.${Math.abs(tenths % 10)}`;
}

/** 123450 → "1,234.50" (no currency). */
export function formatBirrAmount(santim: number): string {
  const sign = santim < 0 ? '-' : '';
  const abs = Math.abs(Math.round(santim));
  return `${sign}${groupThousands(Math.trunc(abs / 100))}.${String(abs % 100).padStart(2, '0')}`;
}

/** Birr value for an input field: 6550 → "65.50". */
export function birrInputValue(santim: number | null | undefined): string {
  if (santim === null || santim === undefined) return '';
  const abs = Math.abs(santim);
  return `${santim < 0 ? '-' : ''}${Math.trunc(abs / 100)}.${String(abs % 100).padStart(2, '0')}`;
}

/** G-2: `1,234.50 ብር` (Amharic UI), `ETB 1,234.50` (English UI). */
export function formatBirr(santim: number, locale: 'am' | 'en' | string): string {
  const amount = formatBirrAmount(santim);
  return locale === 'am' ? `${amount} ብር` : `ETB ${amount}`;
}
